import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import CurrencySign from 'components/price/CurrencySign.component';
import DELAY from 'constants/delay.const';
import { withTranslation } from 'providers/GILT.provider';
import { selectCheckoutBookingData } from 'selectors/checkout.selectors';
import getAnyOrVoid from 'utils/accessors/getAnyOrVoid.util';
import { firstOf } from 'utils/arrays/elements.util';
import fromAnyToLowerCase from 'utils/converters/any/fromAnyToLowerCase.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import delayed from 'utils/functions/delayed.util';
import asObject from 'utils/objects/asObject.util';

import {
  changeServiceDiscountType,
  updateServiceDiscount,
  updateServicePrice,
  updateServiceStylistId,
} from 'checkout/actions';
import DropdownSearch from 'checkout/components/DropdownSearch.component';

const fromPropsToService = (props) => {
  const { bookingData, service, services } = props ?? {};

  const serviceId = service.id;
  if (serviceId) {
    const found = firstOf(services.filter((s) => s.id === serviceId));
    if (found) return found;
  }

  return {
    category: '',
    processing: '',
    role_executions: [],
    variable_pricing: false,
    currency: '',
    duration: '',
    name: bookingData?.service?.name || props.t('checkout.checkoutClient.services'),
    order: 0,
    price: service?.price,
    salon_id: bookingData?.appointment?.salon_id,
    service_category_id: -1,
    status: '',
    stylist_id: service?.stylistId,
  };
};

class CheckoutService extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      service: fromPropsToService(props),
      editStylistModal: false,
      price: props.service.price.toString(),
      displayMobile: true,
      inputStylistChange: false,
      discountType: 'absolute',
      searchQuery: '',
      oldSearchQuery: '',
      isSearchBlurred: false,
      isDropdownOpen: false,
      isSearchQueryDirty: false,
    };
  }

  componentDidMount() {
    this.props.updateServicePrice(this.props.clientIndex, this.props.index, this.props.service.price, true);
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const oldStylists = this.props.stylists;
    const oldStylistId = asObject(this.props.service).stylistId;

    const newStylists = nextProps.stylists;
    const newStylistId = asObject(nextProps.service).stylistId;

    const oldSelectedStylist = oldStylists.find(({ id }) => id === oldStylistId);
    const newSelectedStylist = newStylists.find(({ id }) => id === newStylistId);

    const newSearchQuery = asObject(newSelectedStylist).user_name;
    const oldSearchQuery = asObject(oldSelectedStylist).user_name;
    const isDirty = this.state.isSearchQueryDirty;

    if (!isDirty || newSearchQuery !== oldSearchQuery) {
      this.setState({ searchQuery: newSearchQuery, isSearchQueryDirty: false });
    }
  }

  onStylistSelect = (stylistId, stylistName) => {
    if (stylistId && this.state.isSearchBlurred) {
      this.props.updateServiceStylistId(this.props.clientIndex, this.props.index, stylistId);
      this.setState({
        inputStylistChange: true,
        searchQuery: stylistName,
        isDropdownOpen: false,
        isSearchBlurred: false,
      });
    }
  };

  onSearchStylist = (event) =>
    void this.setState({
      searchQuery: fromAnyToString(getAnyOrVoid('target.value', event)),
      isSearchQueryDirty: true,
      isDropdownOpen: true,
    });

  onClickSearchStylist = () => {
    const isOpen = !this.state.isDropdownOpen;

    void this.setState({
      searchQuery: '',
      oldSearchQuery: this.state.searchQuery,
      isSearchQueryDirty: true,
      isSearchBlurred: isOpen,
      isDropdownOpen: isOpen,
    });
  };

  onDropdownClickOutside = () =>
    void delayed(DELAY.fast, () => {
      if (this.state.isSearchBlurred) {
        this.setState({
          isDropdownOpen: false,
          isSearchBlurred: false,
          searchQuery: this.state.oldSearchQuery,
        });
      }
    });

  onDelete = () => void this.props.onDelete(this.props.index);
  setCursorPosition = (input) => void input.setSelectionRange(input.value.length, input.value.length);
  toggleStylistEdit = () => void this.setState({ editStylistModal: !this.state.editStylistModal });
  showRow = () => void this.setState({ displayMobile: !this.state.displayMobile });

  changePrice = (price) => {
    if (price) {
      this.props.updateServicePrice(this.props.clientIndex, this.props.index, parseFloat(price), false);
      this.setState({ price });
    } else {
      this.setState({ price: '' });
    }
  };

  toggleMoneyPercents = () => {
    const newType = this.props.service.discountType === 'absolute' ? 'percentage' : 'absolute';
    this.setState({ discountType: newType });
    this.props.changeServiceDiscountType(this.props.clientIndex, this.props.index, newType);
  };

  discountUpdate = (event) => {
    let discount = event.target.value;
    if (discount === '') {
      discount = 0;
    } else if (Number.isNaN(Number(discount))) {
      return;
    }
    if (this.state.discountType === 'percentage') {
      if (parseFloat(discount) > 100) {
        discount = 100;
      }
    } else if (parseFloat(discount) > parseFloat(this.state.price)) {
      discount = parseFloat(this.state.price);
    }
    this.props.updateServiceDiscount(this.props.clientIndex, this.props.index, discount);
  };

  render() {
    const { searchQuery, isDropdownOpen, inputStylistChange, displayMobile } = this.state;
    const { showRemove, index, stylists, t } = this.props;

    const { discountType, discount, finalPrice } = this.props?.service ?? {};
    const price = inputStylistChange ? this.props?.service?.price : this.state?.price;
    const serviceName = this.state.service?.name;

    const searchString = fromAnyToLowerCase(searchQuery);
    const renderedStylists = stylists
      .filter(
        (s) =>
          s.status !== 'deleted' &&
          s.has_column &&
          (searchQuery === '' || fromAnyToLowerCase(s.user_name).includes(searchString)),
      )
      .map((stylist) => (
        <li
          tabIndex={0}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
          role="button"
          onClick={() => this.onStylistSelect(stylist.id, stylist.user_name)}
          key={stylist.id}
        >
          {stylist.user_name}
        </li>
      ));

    return (
      <div data-bem="CheckoutService" className="tr" data-index={index}>
        <div className="cell-product">
          <span>{serviceName}</span>
        </div>
        <div className={displayMobile ? 'cell-staff hide' : 'cell-staff '}>
          <span className="v-in-mobile">{t('checkout.checkoutClient.staffMember')}</span>

          <DropdownSearch
            placeholder={t('checkout.checkoutClient.dropdownSearchPlaceholder')}
            type="text"
            isOpen={isDropdownOpen}
            value={searchQuery}
            stylistList={renderedStylists}
            onChange={this.onSearchStylist}
            onClick={this.onClickSearchStylist}
            onBlur={this.onDropdownClickOutside}
          />
        </div>
        <div className="cell-price">
          <div className="product-discount">
            <CurrencySign />
            <input
              type="text"
              className="chk-popup-col3-input"
              onChange={(e) => {
                this.changePrice(e.target.value);
              }}
              onClick={(e) => {
                this.setCursorPosition(e.target);
                this.setState({ inputStylistChange: false });
              }}
              value={price}
              autoComplete="off"
            />
          </div>
        </div>
        <div className={displayMobile ? 'cell-discount hide' : 'cell-discount'}>
          <span className="v-in-mobile">{t('checkout.checkoutClient.discount')}</span>
          <div className="discount">
            <input
              type="text"
              className="chk-amount-input"
              placeholder="0.00"
              value={discount || ''}
              onChange={this.discountUpdate}
              autoComplete="off"
            />
            <button
              className={cn({
                'chk-tog-btn chk-cash-btn': true,
                'chk-selected': discountType === 'absolute',
              })}
              onClick={() => (discountType === 'absolute' ? null : this.toggleMoneyPercents())}
            >
              <CurrencySign />
            </button>
            <button
              className={cn({
                'chk-tog-btn chk-percent-btn': true,
                'chk-selected': discountType === 'percentage',
              })}
              onClick={() => (discountType === 'percentage' ? null : this.toggleMoneyPercents())}
            >
              %
            </button>
          </div>
        </div>
        <div
          className={cn({
            'cell-total hide': displayMobile,
            'cell-total': !displayMobile,
          })}
        >
          <span className="v-in-mobile">{t('checkout.checkoutClient.total')}</span>
          <div className="total">
            <div>
              <CurrencySign value={finalPrice} />
            </div>
          </div>
        </div>
        <div className="cell-last">
          <div className="v-in-mobile btn-show-options" role="button" tabIndex={0} onClick={this.showRow}>
            {displayMobile ? t('checkout.checkoutClient.optionsButton') : t('common.buttons.done')}
          </div>
          {showRemove && (
            <span className="btn-remove-service" role="button" tabIndex={0} onClick={this.onDelete}>
              {t('common.buttons.removeUpp')}
            </span>
          )}
        </div>
      </div>
    );
  }
}

// this extra export is needed for tests that provide their own mock for connect()
export { CheckoutService as CheckoutService4Test };

const CheckoutServiceHoc = connect(
  (state) => ({
    bookingData: selectCheckoutBookingData(state),
  }),
  mapActionsToDispatchToProps({
    changeServiceDiscountType,
    updateServiceDiscount,
    updateServicePrice,
    updateServiceStylistId,
  }),
)(withTranslation()(CheckoutService));

export default CheckoutServiceHoc;
